// Deterministic text canonicalization for EQUALITY comparison only.
//
// This module produces a COMPARISON KEY (canonical form) used to decide whether two text values are
// "the same" for drift detection. It is strictly compare-only: the key is never rendered to the user
// and never persisted. Callers keep the original value for display/save and use the key only for `===`.
// No fuzzy matching -- this is deterministic set/equality canonicalization.
//
// Two layers (industry-standard shape -- cf. a Lucene analyzer chain, a SQL `_CI_AI` collation, an MDM
// match key):
//   1. canonicalizeText  -- GENERIC, Unicode-standards-based (NFKC, whitespace, punctuation, case).
//   2. applyLksiAliases  -- LK/SI BUSINESS equivalences (a small reviewed table), applied on top.
//
// ---------------------------------------------------------------------------------------------------
// THE COMPATIBILITY (ACCEPTANCE-PRESERVATION) INVARIANT
// ---------------------------------------------------------------------------------------------------
// The Season Audit merge resets a row's `accepted` flag when a compared field drifts. To guarantee a
// normalization change never un-accepts a currently-accepted row, the comparison key must be a
// REFINEMENT of today's normalization:
//
//     legacyNormalizeName(a) === legacyNormalizeName(b)   =>   comparisonKey(a) === comparisonKey(b)
//
// i.e. every equivalence the current cleaner creates is preserved. We guarantee this by making the
// legacy-compatible profile FACTOR THROUGH the legacy normalization: it applies the exact legacy strip
// first, then adds only case-fold + whitespace-collapse (folds that can merge more values but never
// split any). Because comparisonKey(x) = g(legacyNormalizeName(x)) for a pure g, the invariant holds
// for ALL inputs regardless of stage ordering. `textCanonicalization.test.js` asserts it as a property.
//
// DELIBERATE EXCLUSION: the legacy-compatible profile does NOT apply NFKC or accent folding. The
// current cleaner `[^a-zA-Z0-9 ]` *deletes* non-ASCII characters (so today an accented letter or a
// full-width digit is dropped); NFKC/accent folding would instead *preserve and normalize* them,
// splitting an existing equivalence class and violating the invariant. NFKC/standardize/accent
// therefore stay in the generic library for a FUTURE standardize profile (with its own migration),
// never in the acceptance-basis key.

// ------------------------------ character helpers (ASCII source, no literal specials) --------------

// Build a character-class regex from code points -- keeps this source pure ASCII and avoids any
// dependency on literal invisible/Unicode characters or fragile \u escapes in the file.
const charClass = (...codePoints) => new RegExp("[" + String.fromCharCode(...codePoints) + "]", "g");

const toStringOrEmpty = (value) => (value === null || value === undefined ? "" : String(value));

// ------------------------------ generic primitive stages (each pure) -------------------------------

// Zero-width / default-ignorable format characters: invisible, and a classic
// "looks-identical-compares-unequal" bug. Soft hyphen, ZWSP, ZWNJ, ZWJ, word joiner, BOM.
const INVISIBLE = charClass(0x00ad, 0x200b, 0x200c, 0x200d, 0x2060, 0xfeff);

// Variant punctuation -> canonical ASCII (meaning-preserving standardization).
const PUNCTUATION_STANDARDIZATION = [
  [charClass(0x2018, 0x2019, 0x201a, 0x201b), "'"], // single curly / low quotes -> apostrophe
  [charClass(0x201c, 0x201d, 0x201e, 0x201f), '"'], // double curly / low quotes -> quote
  [charClass(0x2013, 0x2014, 0x2015, 0x2212), "-"], // en / em / horizontal bar / minus -> hyphen
  [new RegExp(String.fromCharCode(0x2026), "g"), "..."], // ellipsis -> three dots
];

// Legacy strip: remove everything that is not an ASCII letter, digit, or space. This is the exact
// class the current cleaner uses (`[^a-zA-Z0-9 ]`), so it also deletes non-ASCII letters/digits --
// which is what makes the legacy-compatible profile a refinement (see header note).
const NON_ASCII_ALNUM_OR_SPACE = /[^a-zA-Z0-9 ]/g;

const applyNfkc = (s) => s.normalize("NFKC");
const stripInvisible = (s) => s.replace(INVISIBLE, "");
const standardizePunctuation = (s) =>
  PUNCTUATION_STANDARDIZATION.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), s);
const stripPunctuation = (s) => s.replace(NON_ASCII_ALNUM_OR_SPACE, "");
const foldAccents = (s) => s.normalize("NFD").replace(/\p{M}/gu, "").normalize("NFC");
const foldCase = (s) => s.toLowerCase(); // locale-INDEPENDENT (NOT toLocaleLowerCase -- Turkish dotless-i)
const collapseWhitespace = (s) => s.replace(/\s+/gu, " ").trim();

// ------------------------------ the generic canonicalizer ------------------------------------------

// The full generic pipeline. Each stage is individually toggleable so different profiles / fields can
// opt in or out. `standardizePunctuation` and `stripPunctuation` are mutually exclusive by intent;
// when both are requested, strip wins (the legacy-compatible profile).
export function canonicalizeText(value, options = {}) {
  const {
    nfkc = true,
    removeInvisible = true,
    standardizePunctuation: doStandardize = true,
    stripPunctuation: doStrip = false,
    foldAccents: doAccents = true,
    foldCase: doCase = true,
    collapseWhitespace: doCollapse = true,
  } = options;

  let s = toStringOrEmpty(value);
  if (nfkc) s = applyNfkc(s);
  if (removeInvisible) s = stripInvisible(s);
  if (doStrip) s = stripPunctuation(s);
  else if (doStandardize) s = standardizePunctuation(s);
  if (doAccents) s = foldAccents(s);
  if (doCase) s = foldCase(s);
  if (doCollapse) s = collapseWhitespace(s);
  return s;
}

// ------------------------------ profiles -----------------------------------------------------------

// LEGACY-COMPATIBLE profile: reproduces the current cleaner (ASCII strip) and adds ONLY case-fold +
// whitespace-collapse. Factors through legacyNormalizeName -> the compatibility invariant holds by
// construction. This is the acceptance-basis key.
export const LEGACY_COMPATIBLE_PROFILE = Object.freeze({
  nfkc: false,
  removeInvisible: true,
  standardizePunctuation: false,
  stripPunctuation: true,
  foldAccents: false,
  foldCase: true,
  collapseWhitespace: true,
});

// GENERIC / "ideal" standardize profile: full Unicode canonicalization, standardizing (not stripping)
// punctuation. NOT used for the acceptance-basis key (it would split existing equivalence classes) --
// available for future consumers / a Phase-3 standardize migration.
export const STANDARDIZE_PROFILE = Object.freeze({
  nfkc: true,
  removeInvisible: true,
  standardizePunctuation: true,
  stripPunctuation: false,
  foldAccents: true,
  foldCase: true,
  collapseWhitespace: true,
});

// ------------------------------ LK/SI business layer -----------------------------------------------

// Reviewed domain equivalences, applied to an already-generic-canonical key. DELIBERATELY EMPTY for
// now: the mechanism ships and is tested, but activating any alias is a separate reviewed decision
// (an alias merges values, so it must pass the collision review first). Aliases operate on the
// space-delimited token stream of a canonical key. Order matters; longer phrases first.
export const ACTIVE_LKSI_ALIASES = Object.freeze([
  // Example (INACTIVE): ["skills institute", "si"], ["lil kickers", "lk"], ["yrs", "years"], ...
]);

// Whole-phrase replacement on a canonical (already lowercased, single-spaced) key. Pure; identity when
// the table is empty. `aliases` is injectable for testing.
export function applyLksiAliases(canonicalKey, aliases = ACTIVE_LKSI_ALIASES) {
  if (!aliases || aliases.length === 0) return canonicalKey;
  let key = ` ${canonicalKey} `; // sentinel spaces so word-boundary replacement is uniform
  for (const [from, to] of aliases) {
    key = key.split(` ${from} `).join(` ${to} `);
  }
  return key.trim().replace(/\s+/g, " ");
}

// ------------------------------ public comparison keys ---------------------------------------------

// The comparison key for a free-text NAME / description field. Acceptance-basis (legacy-compatible +
// case/whitespace), then the (currently empty) business layer.
export function comparisonKey(value) {
  return applyLksiAliases(canonicalizeText(value, LEGACY_COMPATIBLE_PROFILE));
}

// The comparison key for a LEGACY IDENTIFIER. Intentionally weaker than names (no case/punct/accent
// folding): trim, and treat the legacy "0" sentinel as "" so a saved 0 vs a warehouse null/empty id is
// NOT drift. Identical to the current `normalizeLegacyId`; unchanged in Phase 1.
export function canonicalizeId(value) {
  if (value === null || value === undefined) return "";
  const s = String(value).trim();
  return s === "0" ? "" : s;
}

// ------------------------------ legacy baselines (for tests + diagnostic) --------------------------

// The CURRENT effective name normalization = Layer-A strip (`[^a-zA-Z0-9 ]`) then trim (the merge's
// `normalizeText` is trim, applied to already-stripped values). This is the compatibility baseline the
// invariant is stated against. Exposed so tests and the diagnostic compare against real current behavior.
export function legacyNormalizeName(value) {
  return toStringOrEmpty(value).replace(NON_ASCII_ALNUM_OR_SPACE, "").trim();
}

// The CURRENT id normalization (mirror of SeasonAudit `normalizeLegacyId`). Equal to canonicalizeId.
export function legacyNormalizeId(value) {
  return canonicalizeId(value);
}

// The CURRENT trim-only comparator (mirror of SeasonAudit `normalizeText`) -- used for dates/facilityId,
// which are NOT changing. Exposed for the diagnostic so those fields are compared exactly as today.
export function legacyTrim(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}
